import {AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, DestroyRef} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute, Router} from '@angular/router';
import {DatePipe, Location} from '@angular/common';
import {Title} from '@angular/platform-browser';
import {take} from 'rxjs/operators';

import {InvoiceQuoteVm} from './invoice-form-quote.vm';
import {
  DOC_TYPE_QUOTE,
  NEW_DOC_QUOTE,
  NEW_DOC_AR,
  NEW_DOC_SALES_ORDER,
  PAYMENT_BATCH_TYPE_AR,
  SUBLEDGER_QUOTES
} from '../transaction.constants';
import {cleanAndSort, hasFormChanges} from '../transaction-form.utils';

import {DataService} from '../../../Services/data.service';
import {StorageService} from '../../../Services/storage.service';
import {BsToastService} from '../../../Services/bs-toast-service';
import {DecimalNewPipe} from '../../common/DecimalNewPipe';
import {ConfirmDialogService} from '../../../Services/confirm-dialog.service';
import {CustomFilterPipe} from '../../common/custom-filter.pipe';
import {PreferencesService} from '../../../Services/preferences.service';
import {TaxEngineService} from '../../../Services/tax-engine.service';
import {RoutingTrackerService} from '../../../Services/routing-tracker.service';
import {NavigationService} from '../../../Services/navigation.service';
import {PrintBuilderService} from '../../../Services/print-builder.service';
import {DuplicateCheckerService} from '../../../Services/duplicate-checker.service';
import {PermissionsService} from '../../../Services/permissions.service';
import {EmailModel} from '../../../DataModels/email-model';
import {SendEmailModalComponent} from '../../common/send-email-modal/send-email-modal.component';
import {AuditTrailModalComponent} from '../../common/audit-trail-modal/audit-trail-modal.component';
import {UnsavedChangesComponent} from '../../unsaved-changes/unsaved-changes/unsaved-changes.component';
import {CanComponentDeactivate} from '../../../Interfaces/unsaved-changes.interface';
import {TypeaheadBase} from '../../common/typeahead/typeahead-base';

import {TransactionCalculationService} from '../../../Services/transaction-calculation.service';
import {TransactionTaxService} from '../../../Services/transaction-tax.service';
import {TransactionValidationService} from '../../../Services/transaction-validation.service';
import {TransactionSourceBuilderService} from '../../../Services/transaction-source-builder.service';
import {TransactionEntityService} from '../../../Services/transaction-entity.service';
import {TransactionTermService} from '../../../Services/transaction-term.service';


/**
 * Route: transactions/invoice/quote/form
 * New document: ?invoiceId=newQuote
 * Existing document: ?invoiceId={numericId}
 *
 */
@Component({
  selector: 'app-invoice-form-quote-page',
  templateUrl: './invoice-form-quote.component.html',
  styleUrls: ['./invoice-form-quote.component.css'],
  standalone: false
})
export class InvoiceFormQuotePageComponent extends TypeaheadBase implements OnInit, AfterViewInit, OnDestroy, CanComponentDeactivate {

  // ---- Document type flags ----
  get isQuote(): boolean {
    return true;
  }

  get isAdmin(): boolean {
    return this.permissionsService.Get('Admin');
  }

  // ---- View model ----
  vm: InvoiceQuoteVm = {} as InvoiceQuoteVm;


  // ---- Query params ----
  invoiceId: string;
  documentId: string;
  batchId: any;
  batchTypeId: any = DOC_TYPE_QUOTE;

  // ---- State ----
  submitted = false;
  isSaving = false;
  newDoc = false;
  originalFormData: any;
  disablePrintButton = false;
  disableEmailButton = false;
  developerView = false;
  private suppressGuardOnce = false;
  protected readonly window = window;

  // ---- Index maps for fast lookup ----
  private accountByIdStr = new Map<string, any>();
  private accountByIdNum = new Map<number, any>();
  private itemByIdStr = new Map<string, any>();
  private itemByIdNum = new Map<number, any>();
  private companyByIdStr = new Map<string, any>();
  private companyByIdNum = new Map<number, any>();

  // ---- Typeahead searches ----
  inventoryItemTypeaheadSearch = (text$: any) => this.typeaheadSearch(text$, () => this.vm.inventoryItemName, 10);
  inventoryItemInputFormatter = (value: string) => value?.split(' ')[0] ?? value;
  accountTypeaheadSearch = (text$: any) => this.typeaheadSearch(text$, () => this.vm.accountName, 10);
  entityTypeaheadSearch = (text$: any) => this.typeaheadSearch(text$, () => this.vm.entityName, 10);

  // ---- Email ----
  reportDataToSendInEmail: EmailModel = new EmailModel();
  emailDocumentName = 'Quote';

  // ---- Button config ----
  buttonConfig: any = {
    save: true, close: true, reset: false, delete: true,
    saveNew: true, saveClose: true, print: true, email: true,
  };

  @ViewChild('unsavedChanges') unsavedChanges!: UnsavedChangesComponent;
  @ViewChild(SendEmailModalComponent, {read: ElementRef}) private localSendEmailModalHost: ElementRef;
  @ViewChild(AuditTrailModalComponent) private auditTrailModal: AuditTrailModalComponent;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private localStorage: StorageService,
    private tableDataService: DataService,
    private notification: BsToastService,
    private datePipe: DatePipe,
    private titleService: Title,
    private decimalPipe: DecimalNewPipe,
    private confirmDialogService: ConfirmDialogService,
    public cFilter: CustomFilterPipe,
    public pre: PreferencesService,
    public taxEngine: TaxEngineService,
    public prevUrl: RoutingTrackerService,
    private printBuilder: PrintBuilderService,
    private location: Location,
    private duplicateCheckerService: DuplicateCheckerService,
    private nav: NavigationService,
    private permissionsService: PermissionsService,
    private calculationService: TransactionCalculationService,
    private taxService: TransactionTaxService,
    private validationService: TransactionValidationService,
    private sourceBuilderService: TransactionSourceBuilderService,
    private entityService: TransactionEntityService,
    private termService: TransactionTermService,
    private destroyRef: DestroyRef,
  ) {
    super();
    this.initVm();
  }

  // ---------------------------------------------------------------------------
  // VM initialisation
  // ---------------------------------------------------------------------------

  private initVm(): void {
    /** @vmProp accountFilter - GL account filter state (search_string, account_id, type_name).
     *  Consumers: modal-select-account[vm], line-item account typeahead, fetchLookupData */
    this.vm.accountFilter = {search_string: '', account_id: ''};

    /** @vmProp item - The main document object (header, batch, lines, applied).
     *  Consumers: entire form template, save(), hasChanges(), printDocument(), setEmailData() */
    this.vm.item = {
      header: {payment_applied_total: 0},
      batch: {type_id: DOC_TYPE_QUOTE},
      lines: [],
      applied: [],
    };

    /** @vmProp Preferences - Company/user preferences loaded from API.
     *  Consumers: initializeViewModelDefaults(), setCutOffDates() */
    this.vm.Preferences = {};

    /** @vmProp linkedAttachments - File attachments linked to this document.
     *  Consumers: app-attachments[vm] */
    this.vm.linkedAttachments = [];

    /** @vmProp account - Full GL account list (id, number, name, type_name).
     *  Consumers: modal-select-account[vm], accountTypeaheadSearch, buildAccountName() */
    this.vm.account = [];

    /** @vmProp uom - Unit-of-measure lookup list.
     *  Consumers: line-item UOM dropdown */
    this.vm.uom = [];

    /** @vmProp bank - Bank account list for payment entry.
     *  Consumers: app-invoice-form-ap-ap[vm] */
    this.vm.bank = [];

    /** @vmProp company - Company list for multi-company support.
     *  Consumers: company dropdown, changeCompany(), getCompanyById() */
    this.vm.company = [];

    /** @vmProp currency - Currency lookup list.
     *  Consumers: currency dropdown, currencyChange() */
    this.vm.currency = [];

    /** @vmProp entity - Entity (customer) lookup list for typeahead.
     *  Consumers: entityTypeaheadSearch, modal-select-entity[vm], spliceReceivableEntities() */
    this.vm.entity = [];

    /** @vmProp entityType - Entity type lookup list (Customer, Vendor, etc.).
     *  Consumers: modal-select-entity[vm] */
    this.vm.entityType = [];

    /** @vmProp inventoryItem - Full inventory item list (id, code, name, revenue_account_id, tax ids).
     *  Consumers: inventoryItemTypeaheadSearch, modal-select-item[vm], buildInventoryItemName() */
    this.vm.inventoryItem = [];

    /** @vmProp location - Location/warehouse lookup list.
     *  Consumers: location dropdown, changeLocation() */
    this.vm.location = [];

    /** @vmProp salesperson - Salesperson lookup list.
     *  Consumers: salesperson dropdown on header */
    this.vm.salesperson = [];

    /** @vmProp rfType - Reference field type lookup (rf1–rf5 category labels).
     *  Consumers: reference field dropdowns */
    this.vm.rfType = [];

    /** @vmProp rf1–rf5 - Reference field value lists (one per reference field slot).
     *  Consumers: rf1–rf5 dropdowns on header */
    this.vm.rf1 = [];
    this.vm.rf2 = [];
    this.vm.rf3 = [];
    this.vm.rf4 = [];
    this.vm.rf5 = [];

    /** @vmProp tax - Active tax code list (id, name, rate, expired, etc.).
     *  Consumers: line-item tax dropdowns, calculateTaxValues(), clearZeroTaxRefs() */
    this.vm.tax = [];

    /** @vmProp taxRate - Flattened tax-rate records (tax_id, rate, effective_date, expiry_date).
     *  Consumers: calculateTaxValues(), clearZeroTaxRefs() via taxService.getTaxRate() */
    this.vm.taxRate = [];

    /** @vmProp term - Payment term lookup list.
     *  Consumers: term dropdown, termChange() */
    this.vm.term = [];

    /** @vmProp user - User list (for salesperson assignment and audit).
     *  Consumers: salesperson dropdown */
    this.vm.user = [];

    /** @vmProp payment - AR payment entry object (batch, header, detail).
     *  Consumers: app-invoice-form-ap-ap[vm], saveDocument() */
    this.vm.payment = {batch: {type_id: PAYMENT_BATCH_TYPE_AR, description: 'Batch'}, header: {}, detail: [{id: 1}]};

    /** @vmProp inventoryItemFilter - Filter state for inventory item modal/typeahead.
     *  Consumers: modal-select-item[vm] */
    this.vm.inventoryItemFilter = {};

    /** @vmProp entityFilter - Filter state for entity (customer) modal/typeahead.
     *  Consumers: modal-select-entity[vm] */
    this.vm.entityFilter = {};

    /** @vmProp searchRecentItems - Recent items search state required by modal-entity-history.
     *  Consumers: modal-entity-history[vm] */
    this.vm.searchRecentItems = {};

    /** @vmProp searchTopItems - Top items search state required by modal-entity-history.
     *  Consumers: modal-entity-history[vm] */
    this.vm.searchTopItems = {};

    /** @vmProp deleted - Tracks deleted detail line IDs for PATCH/DELETE on save.
     *  Consumers: saveExistingArDocument() */
    this.vm.deleted = [];

    /** @vmProp item.applied - Applied payment records displayed in the applied-payments table.
     *  Consumers: app-invoice-form-ap-ap[vm], syncAppliedPaymentTotal() */
    this.vm.item.applied = [];

    /** @vmProp is_init - Flag: true once all lookup data has finished loading.
     *  Consumers: template *ngIf guards, postFetchProcessing() */
    this.vm.is_init = false;

    /** @vmProp buttonsDisabled - Disables action buttons when document is locked/batch-locked.
     *  Consumers: template button [disabled] bindings */
    this.vm.buttonsDisabled = false;

    /** @vmProp subLedger - Subledger constant (AR) used by payment and entity components.
     *  Consumers: app-invoice-form-ap-ap[vm], modal-select-entity[vm] */
    this.vm.subLedger = SUBLEDGER_QUOTES;

    /** @vmProp invoiceForm - Flag indicating this vm is used in an invoice form context.
     *  Consumers: shared child components that behave differently in invoice vs other forms */
    this.vm.invoiceForm = true;

    /** @vmProp accountFilter.type_name - Restricts account modal/typeahead to Revenue accounts.
     *  Consumers: modal-select-account[vm], updateAccount() */
    this.vm.accountFilter.type_name = 'Revenue';

    /** @vmProp invoiceTypeName - Localised label ('Receivable') shown in UI headings.
     *  Consumers: template title/heading bindings */
    this.vm.invoiceTypeName = $localize`:Sales Order @@salesOrder:Sales Order`;

    /** @vmProp newAccount - Staging object for inline new-account creation.
     *  Consumers: modal-select-account[vm], saveNewAccount() */
    this.vm.newAccount = {};

    /** @vmProp newItem - Staging object for inline new-inventory-item creation.
     *  Consumers: modal-select-item[vm], saveNewItem() */
    this.vm.newItem = {};

    /** @vmProp newAddress - Staging object for inline new-address creation (defaults to billing).
     *  Consumers: modal-select-address[vm], saveNewAddress() */
    this.vm.newAddress = {billing_address: 'true'};

    /** @vmProp newContact - Staging object for inline new-contact creation.
     *  Consumers: modal-select-contact[vm], saveNewContact() */
    this.vm.newContact = {};

    /** @vmProp newEntity - Staging object for inline new-entity (customer) creation.
     *  Consumers: modal-select-entity[vm], saveNewEntity() */
    this.vm.newEntity = {};

    /** @vmProp history - Entity transaction history summary (balance, pricing, recent/top items).
     *  Consumers: modal-entity-history[vm], loadEntityHistory() */
    this.vm.history = {
      Balance: 0,
      CreditLimit: 0,
      DaysToPay: 0,
      PastDueBalance: 0,
      Pricing: [],
      RecentItems: [],
      TopItems: [],
      Taxes: {},
      Defaults: {}
    };

    /** @vmProp entityTax1Id - First receivable tax ID from the selected entity (transaction-context).
     *  Falls back to item.receivable_tax_1_id if null. Consumers: selectItemModal(), selectItemDropdown() */
    this.vm.entityTax1Id = null;

    /** @vmProp entityTax2Id - Second receivable tax ID from the selected entity (transaction-context).
     *  Falls back to item.receivable_tax_2_id if null. Consumers: selectItemModal(), selectItemDropdown() */
    this.vm.entityTax2Id = null;

    /** @vmProp entityAccountId - Default revenue account ID from the selected entity (transaction-context).
     *  Used when the inventory item has no revenue_account_id. Consumers: selectItemModal(), selectItemDropdown() */
    this.vm.entityAccountId = null;

    /** @vmProp entityAccountName - Display name for entityAccountId.
     *  Consumers: selectItemModal(), selectItemDropdown() */
    this.vm.entityAccountName = null;

    /** @vmProp insertItemModal - Callback to open the item-select modal for a given detail row id.
     *  Consumers: line-item row template (add-item button) */
    this.vm.insertItemModal = (_id: any) => {
    };

    /** @vmProp billToAddress - Label/placeholder for the bill-to address field.
     *  Consumers: address display section in template */
    this.vm.billToAddress = 'Bill To Address';

    /** @vmProp shipToAddress - Label/placeholder for the ship-to address field.
     *  Consumers: address display section in template, loadAndProcessEntityAddresses() */
    this.vm.shipToAddress = 'Ship To Address';

  }

  // ---------------------------------------------------------------------------
  // Angular lifecycle
  // ---------------------------------------------------------------------------

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.invoiceId = params.invoiceId;
      this.documentId = params.documentId;
      this.batchId = params.batchId;
    });

    this.vm.request = {};
    this.vm.paid = false;
    this.vm.isLoaded = false;

    // Normalise legacy invoiceId param → documentId
    if (!this.documentId && this.invoiceId) {
      this.documentId = this.invoiceId;
    }

    this.initData().catch(err => this.handleInitializationError(err));
  }

  ngAfterViewInit(): void {
  }

  ngOnDestroy(): void {
  }

  // ---------------------------------------------------------------------------
  // Unsaved-changes guard
  // ---------------------------------------------------------------------------

  checkForChanges(): boolean {
    if (this.suppressGuardOnce) {
      this.suppressGuardOnce = false;
      return false;
    }
    return this.hasChanges();
  }

  // ---------------------------------------------------------------------------
  // Initialisation pipeline
  // ---------------------------------------------------------------------------

  private async initData(): Promise<void> {
    const [defaultsResult, lookupResult, documentResult] = await Promise.allSettled([
      this.initializeViewModelDefaults(),
      this.fetchLookupData(),
      this.fetchMainDocumentData(),
    ]);

    if (lookupResult.status === 'rejected') {
      this.notification.showErrorToast('Error', 'Failed to load necessary lookup data');
      this.vm.isLoaded = false;
      return;
    }
    if (defaultsResult.status === 'rejected') {
      this.notification.showErrorToast('Error', 'Failed to initialize document defaults');
      this.vm.isLoaded = false;
      return;
    }
    if (this.documentId && documentResult.status === 'rejected') {
      this.notification.showErrorToast('Error', 'Failed to load document data');
      this.vm.isLoaded = false;
      return;
    }

    this.vm.isFullyInitialized = true;
    await this.postFetchProcessing();
    this.titleService.setTitle(this.vm.title || $localize`:Invoice@@invoice:Invoice`);
  }

  private async initializeViewModelDefaults(): Promise<void> {
    if (!this.vm.item) {
      this.vm.item = {header: {payment_applied_total: 0}, batch: {type_id: DOC_TYPE_QUOTE}, lines: [], applied: []};
    }
    if (!this.vm.item.header) {
      this.vm.item.header = {payment_applied_total: 0};
    }
    this.vm.item.header.payment_applied_total ??= 0;
    this.vm.auditTrail = true;
    this.vm.accountFilter.search_string = '';
    this.vm.permissions = {
      ViewReceivableInvoices: this.permissionsService.Get('ViewReceivableInvoices'),
      EditReceivableInvoices: this.permissionsService.Get('EditReceivableInvoices'),
    };
  }

  private fetchLookupData(): Promise<void> {
    return new Promise((resolve, reject) => {
      const lookups = [
        'location', 'entity', 'entity_type', 'uom', 'bank', 'company', 'currency',
        'salesperson', 'payment_type', 'account_subtotal',
        'term', 'sys_user', 'reporting_field_type', 'reporting_field'
      ];

      // Use getData with empty string — DataService appends '/' + '' which is harmless for these endpoints
      const promises = lookups.map(name =>
        this.tableDataService.getData(name, '').pipe(take(1)).toPromise()
          .then((result: any) => ({name, result}))
      );

      // Account: fetch all records explicitly to ensure the modal-select-account is fully populated
      const accountPromise = this.tableDataService.getData('account', '?pageNumber=0&pageSize=999999').pipe(take(1)).toPromise()
        .then((result: any) => ({name: 'account', result}));

      // Tax: use the new aggregate endpoint GET /api/tax/active-with-rates (built in Phase 2).
      // Returns each tax code with its non-expired rates[] embedded — one call replaces two.
      // Falls back to two separate calls (tax + tax_rate) if the endpoint is unavailable.
      const taxPromise = this.tableDataService.getData('tax', 'active-with-rates').pipe(take(1)).toPromise()
        .then((result: any) => ({name: 'tax_with_rates', result}))
        .catch(() => ({name: 'tax_with_rates', result: null}));

      Promise.allSettled([...promises, accountPromise, taxPromise]).then(results => {
        const vmMap: any = {
          location: 'location', entity: 'entity', entity_type: 'entityType',
          uom: 'uom', bank: 'bank', company: 'company', currency: 'currency',
          salesperson: 'salesperson', payment_type: 'paymentType', account: 'account',
          account_subtotal: 'accountSubtotal', term: 'term',
          sys_user: 'user', item: 'inventoryItem',
          reporting_field_type: 'rfType', reporting_field: 'rf',
          // tax_with_rates handled explicitly below
        };

        for (const r of results) {
          if (r.status === 'fulfilled') {
            const {name, result} = r.value as any;
            const data = result?.Data ?? result;
            if (name === 'tax_with_rates') {
              const taxList = Array.isArray(data) ? data : [];
              this.vm.tax = taxList;
              this.vm.taxRate = taxList.flatMap((t: any) =>
                (t.rates ?? []).map((r: any) => ({
                  ...r,
                  tax_id: t.id,
                  tax_name: t.name,
                }))
              ).sort((a: any, b: any) => (a.tax_name ?? '').localeCompare(b.tax_name ?? ''));
            } else if (vmMap[name]) {
              let arr = Array.isArray(data) ? data : [];
              if (name === 'uom') {
                arr = arr.sort((a: any, b: any) => (a.name ?? '').localeCompare(b.name ?? ''));
              }
              this.vm[vmMap[name]] = arr;
            }
          }
        }

        // Split flat reporting_field array into rf1–rf5 by type code
        try {
          const allRfTypes = this.vm.rfType ?? [];
          const allRf = this.vm.rf ?? [];
          const findType = (key: string) => {
            const lower = key.toLowerCase();
            return allRfTypes.find((v: any) => String(v?.code).toLowerCase() === lower)
              || allRfTypes.find((v: any) => String(v?.name).toLowerCase() === lower)
              || allRfTypes.find((v: any) => String(v?.id).toLowerCase() === lower);
          };
          const matchTypeId = (t: any) => (x: any) => String(x?.type_id) === String(t?.id);
          this.vm.rf1 = (findType('rf1') ? allRf.filter(matchTypeId(findType('rf1'))) : []);
          this.vm.rf2 = (findType('rf2') ? allRf.filter(matchTypeId(findType('rf2'))) : []);
          this.vm.rf3 = (findType('rf3') ? allRf.filter(matchTypeId(findType('rf3'))) : []);
          this.vm.rf4 = (findType('rf4') ? allRf.filter(matchTypeId(findType('rf4'))) : []);
          this.vm.rf5 = (findType('rf5') ? allRf.filter(matchTypeId(findType('rf5'))) : []);
        } catch (e) {
          this.vm.rf1 = this.vm.rf1 || [];
          this.vm.rf2 = this.vm.rf2 || [];
          this.vm.rf3 = this.vm.rf3 || [];
          this.vm.rf4 = this.vm.rf4 || [];
          this.vm.rf5 = this.vm.rf5 || [];
        }

        this.buildIndexMaps();
        this.spliceReceivableEntities();
        this.buildEntityName();
        this.buildAccountName();
        this.updateInventoryItem();
        resolve();
      }).catch(reject);
    });
  }

  private fetchMainDocumentData(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.documentId || this.documentId === NEW_DOC_SALES_ORDER) {
        // New document — initialize vm immediately, no fetch needed
        this.setupNewDocument();
        resolve();
        return;
      }
      this.tableDataService.getData('entry', this.documentId).pipe(take(1)).subscribe({
        next: (result: any) => {
          const data = result?.Data ?? result;
          if (data) {
            this.vm.item = data;
          }
          resolve();
        },
        error: (err) => reject(err),
      });
    });
  }

  private async postFetchProcessing(): Promise<void> {
    try {
      if (!this.isNewDocument()) {
        // Existing AR Invoice
        this.vm.status = 'posted';
        this.processReferenceData();
        this.initializeEmptyRows();
        this.spliceReceivableEntities();
        this.createSignatureText();
        this.calcTotal();
        this.setCutOffDates();
        this.termChange();
        await this.setupExistingDocument(this.vm.item);

        // Show form immediately — addresses/history load in background
        this.vm.isLoaded = true;

        const existingEntityId = this.vm?.item?.header?.entity_id;
        if (existingEntityId) {
          this.processEntityData(existingEntityId, true);
          this.loadEntityHistory(existingEntityId);
        }
      } else if (this.isNewDocumentFromExistingSource()) {
        this.vm.status = 'new';
        await this.processNewDocumentFromExistingSourceDocument();
        this.vm.isLoaded = true;
      } else {
        // New empty AR Invoice — vm already initialized by fetchMainDocumentData
        this.vm.status = 'new';
        this.vm.item.header.header_date = this.datePipe.transform(new Date(), 'yyyy-MM-dd');
        this.spliceReceivableEntities();
        try {
          if (this.vm?.item?.header?.company_id) {
            const comp = this.getCompanyById(this.vm.item.header.company_id);
            if (comp?.id !== undefined) {
              this.vm.item.header.company_id = comp.id;
            }
            this.changeCompany();
            this.setCutOffDates();
          }
        } catch { /* keep init resilient */
        }
        this.vm.isLoaded = true;
      }
    } catch (error) {
      console.error('[SO] Post-fetch processing failed:', error);
      this.vm.isLoaded = false;
    }
  }

  private handleInitializationError(error: Error): void {
    console.error('[SO] Initialization failed:', error);
    this.vm.isFullyInitialized = false;
    this.vm.isLoaded = false;
    this.notification.showErrorToast('Unable to initialize the document. Please try again or contact support.');
  }

  // ---------------------------------------------------------------------------
  // New document setup — AR only
  // ---------------------------------------------------------------------------

  private setupNewDocument(): void {
    this.vm.item = {
      header: {payment_applied_total: 0},
      detail: [],
      applied: [],
      batch: {type_id: DOC_TYPE_QUOTE},
      debit: null,
    };
    this.submitted = false;
    this.newDoc = true;
    this.vm.buttonsDisabled = false;
    this.vm.status = 'new';
    this.vm.item.batch.type_id = DOC_TYPE_QUOTE;
    this.batchTypeId = DOC_TYPE_QUOTE;
    this.vm.title = $localize`:Sales Order @@salesOrder:Sales Order` + ' *** ' + $localize`:NEW @@nEW:NEW` + ' ***';
    this.vm.subLedger = SUBLEDGER_QUOTES;
    this.vm.invoiceTypeName = $localize`:Sales Order @@salesOrder:Sales Order`;
    this.vm.accountFilter.type_name = 'Revenue';
    this.vm.payment = {batchTypeId: PAYMENT_BATCH_TYPE_AR};
    this.vm.payment.batch = {type_id: PAYMENT_BATCH_TYPE_AR, description: 'Batch'};
    this.vm.payment.header = {};
    this.vm.payment.detail = [{id: 1}];
    this.vm.newAccount = {};
    this.vm.newItem = {};
    this.vm.newAddress = {billing_address: 'true'};
    this.vm.newContact = {};
    this.initializeEmptyRows();
    this.setHeaderDateToTodayIfEmpty();
    this.originalFormData = structuredClone(this.vm.item);
  }

  // ---------------------------------------------------------------------------
  // Existing document setup — AR only (type_id == '2')
  // ---------------------------------------------------------------------------

  async setupExistingDocument(item: any): Promise<void> {
    this.vm.status = 'posted';
    this.vm.buttonsDisabled = !!item.batch?.locked;
    this.vm.subLedger = SUBLEDGER_QUOTES;
    this.vm.accountFilter.type_name = 'Revenue';
    this.vm.payment = {batchTypeId: PAYMENT_BATCH_TYPE_AR};
    this.vm.payment.batch = {type_id: PAYMENT_BATCH_TYPE_AR, description: 'Batch'};
    this.vm.payment.header = {};
    this.vm.payment.detail = [{id: 1}];

    // Populate account/item inputs on detail lines
    this.vm.item.detail?.forEach((detail: any) => {
      if (detail.control_line === false) {
        const matchAccount = this.getAccountById(detail.account_id);
        const matchItem = this.getItemById(detail.item_id);
        detail.accountInput = matchAccount ? `${matchAccount.number} ${matchAccount.name}` : null;
        detail.itemInput = matchItem ? matchItem.code : null;
      }
    });

    // Credit memo detection
    const isArCredit = this.vm.item.header.is_credit_memo === true ||
      Number(this.vm.item.header.total ?? this.vm.item.header.detail_total ?? 0) < 0;
    this.vm.isCreditInvoice = isArCredit;
    if (isArCredit) {
      this.vm.invoiceTypeName = $localize`:Credit Memo @@creditMemo:Credit Memo`;
      this.vm.title = $localize`:Credit Memo @@creditMemo:Credit Memo` + ' ' + this.vm.item.header.number;
    } else {
      this.vm.invoiceTypeName = $localize`:Sales Order @@salesOrder:Sales Order`;
      this.vm.title = $localize`:Sales Order @@salesOrder:Sales Order` + ' ' + this.vm.item.header.number;
    }

    this.vm.newAccount = {};
    this.vm.newItem = {};
    this.vm.newAddress = {billing_address: 'true'};
    this.vm.newContact = {};
    if (this.vm.item.debit === undefined) {
      this.vm.item.debit = null;
    }
    this.initializeEmptyRows();
    this.ensureRfVisibilityForDetails();
    this.syncAppliedPaymentTotal();
    // Note: do NOT take the unsaved-changes snapshot here for existing documents.
    // processEntityData() will mutate header fields and then call
    // loadAndProcessEntityAddresses(), which takes the definitive snapshot after
    // all async mutations complete. Taking it here would produce a stale snapshot
    // that differs from the fully-loaded state, causing the guard to fire on load.
  }

  // ---------------------------------------------------------------------------
  // Entity loading — uses aggregate endpoint
  // ---------------------------------------------------------------------------

  /**
   * Fetch and split addresses for an entity into bill-to / ship-to dropdown lists.
   * Mirrors the monolith's loadAndProcessEntityAddresses() exactly — called both
   * when loading an existing document (to restore saved address selections) and
   * after saving a new address (to refresh the dropdown with the newly added entry).
   */
  protected loadAndProcessEntityAddresses(entityId: string | number, preferredBillAddressId?: string, preferredShipAddressId?: string): void {
    if (!entityId) {
      return;
    }

    this.vm.billToAddress = [];
    this.vm.shipToAddress = [];

    if (!this.vm.item.header) {
      this.vm.item.header = {} as any;
    }
    if (this.vm.item.header.bill_address_id === undefined) {
      this.vm.item.header.bill_address_id = null;
    }
    if (this.vm.item.header.ship_address_id === undefined) {
      this.vm.item.header.ship_address_id = null;
    }

    this.entityService.loadEntityAddresses(entityId).subscribe({
      next: (result: any) => {
        const data: any = (result && result.Data !== undefined) ? result.Data : result;
        const all = Array.isArray(data) ? data : [];

        const {billList, shipList} = this.entityService.splitAddressesByFlags(all);
        this.vm.billToAddress = billList;
        this.vm.shipToAddress = shipList;

        this.vm.item.header.bill_address_id = this.entityService.resolveBillAddressId(
          this.vm.billToAddress, this.isNewDocument(), this.vm.item.header.bill_address_id, preferredBillAddressId);
        this.vm.item.header.ship_address_id = this.entityService.resolveShipAddressId(
          this.vm.shipToAddress, this.isNewDocument(), this.vm.item.header.ship_address_id, preferredShipAddressId);

        if (!this.isNewDocument()) {
          // Existing doc: wait for tax calculations on all detail lines to settle before snapshotting.
          Promise.all(
            (this.vm.item?.detail ?? []).map((d: any) => this.calculateTaxValues(d.id))
          ).then(() => {
            setTimeout(() => {
              this.originalFormData = structuredClone(this.vm.item);
            }, 500);
          });
        }
      },
      error: (err) => console.error('[SO] loadAndProcessEntityAddresses failed:', err),
    });
  }

  protected processEntityData(entityId: string | number, preserveExisting = false): void {
    if (!entityId) {
      return;
    }
    this.entityService.loadEntityContext(entityId).subscribe({
      next: (ctx: any) => {
        if (!ctx) {
          return;
        }
        // API returns snake_case: { entity, addresses, primary_contact, pricing, balance }
        // entity node now includes company_id, currency_id, term_id, location_id directly.
        const entity = ctx.entity;
        if (!entity?.id) {
          return;
        }

        this.vm.item.header.entity_id = entity.id;
        this.vm.item.header.entity_name = entity.name;
        this.vm.item.header.entityInput = entity.name;
        if (entity.tax_code && !preserveExisting) {
          this.vm.item.header.tax_id = entity.tax_code;
        }

        // term_id — now returned directly by the context endpoint
        if (entity.term_id && !preserveExisting) {
          this.vm.item.header.term_id = entity.term_id;
        }

        // company_id — now returned directly; trigger dependent updates (bank filter, cut-off dates)
        if (entity.company_id && !preserveExisting) {
          this.vm.item.header.company_id = entity.company_id;
          this.changeCompany();
        }

        // currency_id — now returned directly
        if (entity.currency_id && !preserveExisting) {
          this.vm.item.header.currency_id = entity.currency_id;
        }

        // location_id — now returned directly
        if (entity.location_id && !preserveExisting) {
          this.vm.item.header.location_id = entity.location_id;
        }

        if (ctx.pricing?.price_list_id) {
          this.vm.item.header.price_level = ctx.pricing.price_list_id;
        }
        if (ctx.primary_contact) {
          this.vm.item.header.contact_id = ctx.primary_contact.id;
          this.vm.contact = [ctx.primary_contact];
        }

        // Store entity-level receivable taxes and default sales account for use when line items are selected
        this.vm.entityTax1Id = entity.receivable_tax_1_id ?? null;
        this.vm.entityTax2Id = entity.receivable_tax_2_id ?? null;
        this.vm.entityAccountId = entity.revenue_account_id ?? null;
        this.vm.entityAccountName = entity.revenue_account_name ?? null;

        // Warn if entity taxes are expired
        if (this.vm.entityTax1Id) {
          const t1 = this.taxService.getTaxById(this.vm.entityTax1Id, this.vm.tax ?? []);
          if (t1 && this.taxService.isTaxExpired(t1)) {
            this.notification.showWarningToast(`Tax "${t1.name ?? entity.receivable_tax_1_name ?? ''}" on this entity is expired.`);
          }
        }
        if (this.vm.entityTax2Id) {
          const t2 = this.taxService.getTaxById(this.vm.entityTax2Id, this.vm.tax ?? []);
          if (t2 && this.taxService.isTaxExpired(t2)) {
            this.notification.showWarningToast(`Tax "${t2.name ?? entity.receivable_tax_2_name ?? ''}" on this entity is expired.`);
          }
        }

        // Delegate to loadAndProcessEntityAddresses — mirrors the monolith pattern
        this.loadAndProcessEntityAddresses(entity.id);

        this.setHeaderDateToTodayIfEmpty();
        this.loadEntityHistory(entityId as string);
      },
      error: (err) => console.error('[SO] processEntityData failed:', err),
    });
  }

  // ---------------------------------------------------------------------------
  // Save — AR only
  // ---------------------------------------------------------------------------

  async save(): Promise<void> {
    this.validateDocumentData();
    this.submitted = true;
    if (!(await this.validateDocumentBeforeSave())) {
      return;
    }

    // Clear zero-rate tax refs — only when tax rates are loaded; skip if vm.taxRate is empty
    // to avoid incorrectly wiping valid tax IDs when the rate list hasn't loaded yet.
    if (Array.isArray(this.vm?.item?.detail) && (this.vm.taxRate ?? []).length > 0) {
      for (const d of this.vm.item.detail) {
        this.clearZeroTaxRefs(d);
        if (Number(d?.amount || 0) === 0) {
          d.quantity = 0;
        }
      }
    } else if (Array.isArray(this.vm?.item?.detail)) {
      for (const d of this.vm.item.detail) {
        if (Number(d?.amount || 0) === 0) {
          d.quantity = 0;
        }
      }
    }

    await this.removeEmptyDetailLinesBeforeSave();
    this.isSaving = true;

    if (!this.vm.newInvoice && !this.vm.closeInvoice) {
      this.vm.saveInvoice = true;
    }

    // Set debit_credit for new lines (non-UUID ids) and recalculate tax before save
    for (const [x] of Object.entries(this.vm.item.detail)) {
      const lineId = this.vm.item.detail[x].id;
      const isApiLine = lineId && typeof lineId === 'string' && lineId.includes('-');
      if (!isApiLine) {
        this.vm.item.detail[x].debit_credit = Number(this.vm.item.detail[x].amount || 0) * Number(this.vm.item.detail[x].quantity || 0);
      }
      if (!this.vm.item.detail[x].debit_credit) {
        this.vm.item.detail[x].debit_credit = 0;
      }
      if (this.vm.item.detail[x].quantity > 0 && this.vm.item.detail[x].amount > 0) {
        this.calculateTaxValues(this.vm.item.detail[x].id).then();
      }
    }

    // Apply linked signature if none set
    if (!this.vm.item.header.signature && this.vm.linkedSignature) {
      this.vm.item.header.signature = this.vm.linkedSignature;
    }

    if (this.isNewDocument()) {
      await this.saveNewArDocument();
    } else {
      await this.saveExistingArDocument();
    }
  }

  private async saveNewArDocument(): Promise<void> {
    // Credit memo: zero out payment_applied_total
    try {
      const totalVal = Number(this.vm?.item?.header?.total ?? this.vm?.total ?? 0);
      const isCredit = totalVal < 0 || !!this.vm?.isCreditInvoice;
      if (isCredit) {
        this.vm.item.header.payment_applied_total = 0;
        this.vm.item.applied = [];
      }
    } catch { /* ignore */
    }

    this.vm.item.header.is_credit_memo = this.vm.isCreditInvoice;

    this.tableDataService.post('entry', this.vm.item).pipe().subscribe({
      next: (item: any) => {
        this.isSaving = false;
        this.saveDocument(item.id);

        if (this.vm.saveInvoice) {
          this.batchTypeId = DOC_TYPE_QUOTE;
          this.documentId = item.id;
          this.vm.status = 'posted';
          this.initData().then();
          this.updateQueryParam(item.id);
        }

        if (this.vm.newInvoice) {
          this.vm.buttonsDisabled = false;
          this.batchTypeId = DOC_TYPE_QUOTE;
          this.documentId = NEW_DOC_QUOTE;
          this.vm.status = 'new';
          this.initData().then(() => {
            this.createSignatureText();
            try {
              this.originalFormData = structuredClone(this.vm.item);
              this.resetSnapshot();
            } catch { /* ignore */
            }
          });
          this.updateQueryParam(NEW_DOC_QUOTE);
          this.vm.paid = false;
          this.vm.is_init = true;
        }
      },
      error: (error: any) => {
        this.vm.buttonsDisabled = false;
        this.isSaving = false;
        const errorMsg = error?.error?.Message ?? error?.statusText ?? 'Save failed';
        console.error('[SO] POST failed:', errorMsg);
      },
      complete: () => { /* no-op */
      }
    });
  }

  private async saveExistingArDocument(): Promise<void> {
    if (!this.vm.item.header.signature && this.vm.linkedSignature) {
      this.vm.item.header.signature = this.vm.linkedSignature;
    }

    this.vm.item.header.is_credit_memo = !!this.vm.isCreditInvoice;

    const changes: any = {
      detail: this.vm.item.detail,
      salesperson: this.vm.item.salesperson,
      attachments: this.vm.item.attachments,
    };

    const headerChanges: any = {};
    for (const key in this.vm.item.header) {
      if (Object.prototype.hasOwnProperty.call(this.vm.item.header, key)) {
        let value = this.vm.item.header[key];
        if (value != key && key !== 'entityInput') {
          if (key === 'header_date' && !value) {
            value = this.datePipe.transform(new Date(), 'yyyy-MM-ddTHH:mm:ss');
          }
          headerChanges[key] = value;
        }
      }
    }
    if (headerChanges) {
      changes.header = headerChanges;
    }

    if (this.vm.buttonsDisabled) {
      return;
    }

    this.tableDataService.patchWithParamsNew('entry', this.vm.item.header.id, changes)
      .then((item: any) => {
        this.isSaving = false;
        this.vm.buttonsDisabled = false;

        if (this.vm.newInvoice) {
          this.saveDocument(this.vm.item.header.id);
          this.vm.item.attachments = [];
          this.vm.newInvoice = false;
          this.vm.contact = '';
          this.vm.history = {};
          this.vm.billToAddress = '';
          this.vm.shipToAddress = '';
          this.vm.paid = false;
          this.vm.is_init = false;
          this.vm.total = 0;
          this.vm.subtotal = 0;
          this.batchTypeId = DOC_TYPE_QUOTE;
          this.documentId = NEW_DOC_QUOTE;
          this.vm.status = 'new';
          this.initData().then(() => {
            this.createSignatureText();
            try {
              this.originalFormData = structuredClone(this.vm.item);
              this.resetSnapshot();
            } catch { /* ignore */
            }
          });
          this.updateQueryParam(NEW_DOC_QUOTE);
          this.vm.is_init = true;
        } else {
          this.saveDocument(this.vm.item.header.id);
          this.notification.showSuccessToast($localize`:Saved @@saved:Saved`);
          this.auditTrailModal?.loadAuditTrail();
          try {
            this.originalFormData = structuredClone(this.vm.item);
          } catch { /* ignore */
          }
        }
      })
      .catch((error: any) => {
        this.vm.buttonsDisabled = false;
        this.isSaving = false;
        console.error('[SO] PATCH failed:', error);
      });
  }

  // ---------------------------------------------------------------------------
  // Delegated helpers — call Transaction* services or shared utilities
  // ---------------------------------------------------------------------------

  validateDocumentData(): void {
    const rawStatus = this.vm.status ?? '';
    const normalizedStatus = rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1).toLowerCase();
    const result = this.validationService.validateDocumentData(
      this.vm.item?.header ?? {},
      this.vm.item?.detail ?? [],
      normalizedStatus,
      this.vm.balanceDue ?? 0,
      this.vm.subtotal ?? 0,
      this.vm.taxtotal ?? 0,
      this.vm.total ?? 0,
      this.vm.isCreditInvoice ?? false
    );
    this.vm.isDocumentValid = result.isValid;
    if (!result.isValid) {
      for (const msg of result.errors) {
        this.notification.showWarningToast(msg);
      }
    }
  }

  async validateDocumentBeforeSave(): Promise<boolean> {
    if (!this.vm.isDocumentValid) {
      return false;
    }
    const h = this.vm.item?.header;
    if (
      this.isFieldEmpty(h?.entityInput) ||
      this.isFieldEmpty(h?.bill_address_id) ||
      this.isFieldEmpty(h?.header_date) ||
      this.isFieldEmpty(h?.company_id) ||
      this.isFieldEmpty(h?.currency_id) ||
      this.isFieldEmpty(h?.location_id) ||
      this.isFieldEmpty(h?.term_id) ||
      this.isFieldEmpty(h?.description)
    ) {
      this.notification.showWarningToast($localize`:Form Errors - enter the highlighted field(s) @@formErrors:Form Errors - enter the highlighted field(s)`);
      return false;
    }
    return true;
  }

  calcTotal(): void {
    const result = this.calculationService.calculateTotals(this.vm.item?.detail ?? [], this.vm.tax ?? []);
    this.vm.subtotal = result.subtotal;
    this.vm.taxtotal = result.taxtotal;
    this.vm.total = result.total;
    this.vm.invoiceTaxes = result.invoiceTaxes ?? [];
  }

  clearZeroTaxRefs(detail: any): void {
    this.taxService.clearZeroTaxRefs(detail, this.vm.taxRate ?? []);
  }

  async calculateTaxValues(id: any): Promise<void> {
    const d = (this.vm.item?.detail ?? []).find((item: any) => item.id === id);
    if (!d) {
      return;
    }

    if (Number(d.amount || 0) === 0) {
      d.tax_amount_1 = null;
      d.tax_amount_2 = null;
      this.calcTotal();
      return;
    }

    d.tax_amount_1 = d.tax_id_1
      ? this.taxService.calculateSingleTaxAmount(d, 'tax_id_1', this.vm.tax ?? [], this.vm.taxRate ?? []).amount
      : null;
    d.tax_amount_2 = d.tax_id_2
      ? this.taxService.calculateSingleTaxAmount(d, 'tax_id_2', this.vm.tax ?? [], this.vm.taxRate ?? []).amount
      : null;

    this.calcTotal();
  }

  spliceReceivableEntities(): void {
    const filtered = this.entityService.filterReceivableEntities(this.vm.entity ?? [], this.vm.item?.header?.entity_id);
    this.vm.receivableEntities = filtered;
  }

  generateInvoice(): void {
    const id = this.vm?.item?.header?.id;
    if (!id) {
      return;
    }
    const messages: string[] = [$localize`:Convert to AR Invoice? @@convertToArInvoice:Convert to AR Invoice?`, ''];
    this.confirmDialogService.confirmThis(
      messages,
      () => {
        this.router.navigateByUrl('/transactions/invoice/ar/form?invoiceId=' + NEW_DOC_AR + '&batchId=&quoteId=' + id).then();
      },
      () => {
        this.notification.showSuccessToast($localize`:Canceled @@Canceled:Canceled`);
      }
    );
  }

  generateSalesOrder(): void {
    const id = this.vm?.item?.header?.id;
    if (!id) {
      return;
    }
    const messages: string[] = [$localize`:Convert to Sales Order? @@convertToSalesOrder:Convert to Sales Order?`, ''];
    this.confirmDialogService.confirmThis(
      messages,
      () => {
        this.router.navigateByUrl('/transactions/invoice/so/form?invoiceId=' + NEW_DOC_SALES_ORDER + '&batchId=&quoteId=' + id).then();
      },
      () => {
        this.notification.showSuccessToast($localize`:Canceled @@Canceled:Canceled`);
      }
    );
  }

  get canGenerateInvoice(): boolean {
    return !!this.vm?.item?.header?.id
      && !this.vm?.quoteInvoiced
      && !this.vm?.buttonsDisabled;
  }

  get canGenerateSalesOrder(): boolean {
    return !!this.vm?.item?.header?.id
      && !this.vm?.quoteOrdered
      && !this.vm?.buttonsDisabled;
  }

  buildEntityName(): void {
    this.vm.entityName = this.entityService.buildEntityNameList(this.vm.entity ?? []);
  }

  buildInventoryItemName(): void {
    this.vm.inventoryItemName = (this.vm.inventoryItem ?? []).map((i: any) =>
      [i.code, i.name].filter(Boolean).join(' ')
    );
  }

  buildAccountName(): void {
    this.vm.accountName = (this.vm.account ?? []).map((a: any) => `${a.number ?? ''} ${a.name ?? ''}`.trim());
  }

  // ---------------------------------------------------------------------------
  // Utility helpers
  // ---------------------------------------------------------------------------

  isNewDocument(): boolean {
    return this.documentId === NEW_DOC_QUOTE || !this.vm?.item?.header?.id;
  }

  private isNewDocumentFromExistingSource(): boolean {
    return false;
  }

  getAccountById(id: any): any {
    return this.accountByIdStr.get(String(id)) ?? this.accountByIdNum.get(Number(id));
  }

  getItemById(id: any): any {
    return this.itemByIdStr.get(String(id)) ?? this.itemByIdNum.get(Number(id));
  }

  getCompanyById(id: any): any {
    return this.companyByIdStr.get(String(id)) ?? this.companyByIdNum.get(Number(id));
  }

  private buildIndexMaps(): void {
    for (const a of (this.vm.account ?? [])) {
      this.accountByIdStr.set(String(a.id), a);
      this.accountByIdNum.set(Number(a.id), a);
    }
    for (const i of (this.vm.inventoryItem ?? [])) {
      this.itemByIdStr.set(String(i.id), i);
      this.itemByIdNum.set(Number(i.id), i);
    }
    for (const c of (this.vm.company ?? [])) {
      this.companyByIdStr.set(String(c.id), c);
      this.companyByIdNum.set(Number(c.id), c);
    }
  }

  private loadEntityHistory(entityId: string | number): void {
    if (!entityId) {
      return;
    }
    this.tableDataService.getData('entity_history', String(entityId)).pipe(take(1))
      .subscribe((result: any) => {
        this.vm.history = result;
      });
  }

  setHeaderDateToTodayIfEmpty(): void {
    if (!this.vm.item.header.header_date) {
      this.vm.item.header.header_date = this.datePipe.transform(new Date(), 'yyyy-MM-dd');
    }
  }

  private initializeEmptyRows(): void {
    if (!Array.isArray(this.vm.item.detail)) {
      this.vm.item.detail = [];
    }
    const nonControl = this.vm.item.detail.filter((d: any) => !d.control_line);
    if (nonControl.length === 0) {
      this.vm.item.detail.push({id: Date.now(), quantity: 0, amount: 0, control_line: false, tax_amount_1: 0, tax_amount_2: 0});
    }
  }

  private ensureRfVisibilityForDetails(): void {
    // Reporting field visibility — delegate to shared logic when extracted
  }

  private syncAppliedPaymentTotal(): void {
    const applied = Array.isArray(this.vm.item?.applied) ? this.vm.item.applied : [];
    this.vm.item.header.payment_applied_total = applied.reduce(
      (sum: number, a: any) => sum + Number(a.amount ?? 0), 0
    );
  }

  private processReferenceData(): void {
    // Populate reporting field arrays from loaded data
  }

  private createSignatureText(): void {
    // Signature text generation — AR specific
  }

  termChange(): void {
    const due = this.termService.calculateDueDate(this.vm.term, this.vm.item.header.term_id, this.vm.item.header.header_date);
    if (due) {
      this.vm.item.header.due_date = due;
    }
  }

  changeCompany(): void {
    // Filter banks by company — AR specific
  }

  private setCutOffDates(): void {
    const comp = this.getCompanyById(this.vm.item?.header?.company_id);
    this.vm.companyCutOffDate = comp?.cut_off_date ?? this.vm.companyCutOffDate;
    this.vm.companyYearEndDate = comp?.year_end_date ?? this.vm.companyYearEndDate;
  }

  private updateQueryParam(documentIdValue: any): void {
    const url = this.router.createUrlTree([], {
      relativeTo: this.route,
      queryParams: {invoiceId: documentIdValue},
      queryParamsHandling: 'merge',
    }).toString();
    this.location.replaceState(url);
  }

  private saveDocument(headerId: any): void {
    if (!this.vm.paymentAmount || !headerId) {
      return;
    }
    // Payment save logic — AR specific, delegates to tableDataService
    const payment = structuredClone(this.vm.payment);
    payment.header.entry_id = headerId;
    this.tableDataService.post('payment', payment).pipe(take(1)).subscribe({
      next: () => {
        this.vm.paymentAmount = 0;
      },
      error: (err: any) => console.error('[SO] saveDocument failed:', err),
    });
  }

  private async processNewDocumentFromExistingSourceDocument(): Promise<void> {
    this.calcTotal();
    this.initializeEmptyRows();
    this.spliceReceivableEntities();
    this.vm.item.detail?.forEach((d: any) => {
      if (d.control_line === false) {
        if (d.id) {
          this.calculateTaxValues(d.id).then();
        } else {
          // New lines (e.g. from workorder) have no id yet — calculate directly
          if (Number(d.amount || 0) !== 0) {
            d.tax_amount_1 = d.tax_id_1
              ? this.taxService.calculateSingleTaxAmount(d, 'tax_id_1', this.vm.tax ?? [], this.vm.taxRate ?? []).amount
              : null;
            d.tax_amount_2 = d.tax_id_2
              ? this.taxService.calculateSingleTaxAmount(d, 'tax_id_2', this.vm.tax ?? [], this.vm.taxRate ?? []).amount
              : null;
          }
        }
      }
    });
    this.calcTotal();
  }

  private resolveDetailInputs(): void {
    this.vm.item.detail?.forEach((detail: any) => {
      if (detail.control_line === false) {
        const matchAccount = this.getAccountById(detail.account_id);
        const matchItem = this.getItemById(detail.item_id);
        detail.accountInput = matchAccount ? `${matchAccount.number} ${matchAccount.name}` : null;
        detail.itemInput = matchItem ? matchItem.code : null;
        if (!detail.description && matchItem) {
          detail.description = matchItem.name || matchItem.description || '';
        }
        if (!detail.uom && matchItem) {
          detail.uom = matchItem.uom || matchItem.uom_name || '';
        }
        if (!detail.uom_id && matchItem) {
          detail.uom_id = matchItem.uom_id || null;
        }
      }
    });
  }

  private mapDocumentStatus(status: string): string {
    status = status?.toUpperCase();
    const statusMap: Record<string, string> = {
      DRAFT: 'Draft',
      PENDING: 'Pending Approval',
      APPROVED: 'Approved',
      REJECTED: 'Rejected',
      POSTED: 'Posted',
      EDITING: 'Editing',
      DELETED: 'Deleted',
      NEW: 'New',
    };
    return statusMap[status] || 'Unknown';
  }

  private async prepareDocumentFromSource(sourceDoc: any): Promise<void> {
    const tempBatchTypeId = this.batchTypeId;
    this.vm.item = sourceDoc;
    if (!this.vm.item.header) {
      this.vm.item.header = {};
    }
    if (!this.vm.item.batch) {
      this.vm.item.batch = {};
    }
    if (!this.vm.item.detail) {
      this.vm.item.detail = [];
    }
    this.vm.item.header.id = undefined;
    this.vm.item.header.number = undefined;
    this.vm.item.header.signature = undefined;
    this.vm.item.header.batch_id = undefined;
    this.vm.item.batch.id = undefined;
    this.vm.item.batch.type_id = tempBatchTypeId;
    this.vm.item.header.audit_sequence = undefined;
    this.vm.item.header.completed = undefined;
    this.vm.item.header.header_date = this.datePipe.transform(new Date(), 'yyyy-MM-dd');
    this.vm.linkedAttachments = this.vm.item.attachments;
    this.vm.item.attachments = [];
  }

  createCreditMemoFromInvoice(): void {
    const messages: string[] = [
      $localize`:Create Credit Memo? @@createCreditMemo:Create Credit Memo?`,
      '',
    ];
    this.confirmDialogService.confirmThis(
      messages,
      () => {
        this.doCreateCreditMemoFromInvoice();
      },
      () => {
        this.notification.showSuccessToast($localize`:Canceled @@Canceled:Canceled`);
      }
    );
  }

  private doCreateCreditMemoFromInvoice(): void {
    try {
      const sub = String(this.vm?.subLedger || '').toLowerCase();
      const hasId = !!this.vm?.item?.header?.id;
      try {
        const totalVal = Number(this.vm?.item?.header?.total ?? this.vm?.total ?? 0);
        const isExistingCredit = hasId && (sub === 'ap') && totalVal < 0;
        if (isExistingCredit) {
          this.notification.showWarningToast($localize`:Cannot create a Credit Memo from an existing Credit Memo.@@cannotCreateFromCredit:Cannot create a Credit Memo from an existing Credit Memo.`);
          return;
        }
      } catch { /* ignore */
      }

      this.documentId = NEW_DOC_SALES_ORDER;

      const source = structuredClone(this.vm.item);

      if (source?.header) {
        source.header.payment_applied_total = 0;
        source.header.is_credit_memo = true;
      }
      if (Array.isArray(source?.applied)) {
        source.applied = [];
      } else {
        (source as any).applied = [];
      }
      if (Array.isArray(source?.lines)) {
        source.lines = [];
      }
      if (Array.isArray(source?.feed)) {
        source.feed = [];
      }

      const originalNumber = this.vm?.item?.header?.number;
      try {
        const baseDesc = String(source?.header?.description || '').trim();
        const suffix = originalNumber ? ` (Credit for ${originalNumber})` : ' (Credit)';
        source.header.description = (baseDesc ? baseDesc : 'Credit Memo') + suffix;
      } catch { /* ignore */
      }

      this.prepareDocumentFromSource(source).then(() => {
      });
      this.vm.item.batch = {type_id: DOC_TYPE_QUOTE, description: 'new credit memo batch'};
      this.vm.item.header.audit_sequence = undefined;

      try {
        this.calcTotal();
      } catch { /* ignore */
      }

      this.vm.status = this.mapDocumentStatus('new');
      this.vm.isCreditInvoice = true;
      this.vm.title = $localize`:Credit Memo @@creditMemo:Credit Memo`;
      this.newDoc = true;
      this.vm.buttonsDisabled = false;

      this.notification.showSuccessToast($localize`:Credit Memo draft created.@@creditMemoDraftCreated:Credit Memo draft created.`);
    } catch (e) {
      console.error('[SO] Failed to create credit memo from invoice', e);
      this.notification.showErrorToast($localize`:Unable to create Credit Memo from this invoice.@@unableToCreateCreditMemo:Unable to create Credit Memo from this invoice.`);
    }
  }

  private removeEmptyDetailLinesBeforeSave(): Promise<void> {
    return new Promise(resolve => {
      if (Array.isArray(this.vm.item.detail)) {
        this.vm.item.detail = this.vm.item.detail.filter((d: any) => {
          const qty = Number(d.quantity ?? 0);
          const amt = Number(d.amount ?? 0);
          return d.control_line || (qty !== 0 || amt !== 0);
        });
      }
      resolve();
    });
  }

  hasChanges(): boolean {
    return hasFormChanges(this.vm.item, this.originalFormData);
  }

  resetSnapshot(): void {
    this.originalFormData = structuredClone(this.vm.item);
  }

  // ---- Navigation ----
  saveClose(): void {
    this.vm.closeInvoice = true;
    this.save().then(() => this.returnToList());
  }

  onClose(): void {
    this.navigateBackOrList();
  }

  private navigateBackOrList(): void {
    this.nav.backOr('/transactions/invoice/list/quote-refactor');
  }

  private returnToList(): void {
    this.navigateBackOrList();
  }

  deleteItem(): void {
    this.confirmDialogService.confirmThis(
      [$localize`:Delete @@delete:Delete`, $localize`:Are you sure you want to delete this document? @@areyousureyouwanttodeletethisdocument:Are you sure you want to delete this document?`],
      () => {
        this.tableDataService.deleteDataById('entry', this.vm.item.header.id).pipe(take(1)).subscribe({
          next: () => {
            this.notification.showSuccessToast($localize`:Deleted @@deleted:Deleted`);
            this.returnToList();
          },
          error: (err: any) => console.error('[SO] delete failed:', err),
        });
      },
      () => {
        this.notification.showSuccessToast($localize`:Delete Canceled @@deleteCanceled:Delete Canceled`);
      }
    );
  }

  // ---- Email ----
  getDataEmitFromInvoiceToEmail(event: any): void {
    this.reportDataToSendInEmail = event;
  }

  emailDocument(): void {
    this.setEmailData();
  }

  setEmailData(): void {
    const totalVal = Number(this.vm?.item?.header?.total ?? this.vm?.total ?? 0);
    const isCredit = this.vm.isCreditInvoice || totalVal < 0;
    this.emailDocumentName = 'Quote';

    const emailDocObj = {} as EmailModel;
    emailDocObj.print_orientation = '1';
    emailDocObj.convert_attachment_to = 'pdf';
    emailDocObj.fromEmail = '';
    emailDocObj.fromName = 'AcctXErp';
    emailDocObj.header_id = this.documentId;
    emailDocObj.toEmail = this.vm?.primary_contact?.email ?? '';
    emailDocObj.body = 'Please see the attached ' + this.emailDocumentName + ' - ' + this.vm.item.header.number + '.<br><br><br>'
      + 'Best regards,'
      + '<br><br>'
      + 'AcctXErp'
      + '<br><br>';
    emailDocObj.Subject = 'AcctXErp ' + this.emailDocumentName + ' - ' + this.vm.item.header.number;
    emailDocObj.activity_message = this.emailDocumentName;

    try {
      const docType = 'quotes';
      emailDocObj.pdfHtml = this.printBuilder.buildEmailHtml(this.vm, docType);
    } catch {
      emailDocObj.pdfHtml = '';
    }

    const safeName = (this.emailDocumentName || 'document').replace(/\s+/g, '_');
    emailDocObj.FileName = this.documentId != null
      ? `${safeName}_${this.vm.item.header.number}.pdf`
      : `${safeName}.pdf`;

    this.reportDataToSendInEmail = emailDocObj;
  }

  // ---------------------------------------------------------------------------
  // Template-bound methods (modal callbacks, line ops, utilities)
  // ---------------------------------------------------------------------------

  get itemPaid(): boolean {
    return !!this.vm?.paid;
  }

  isFieldEmpty(value: any): boolean {
    return this.validationService.isFieldEmpty(value);
  }

  toggleCreditInvoice(): void {
    this.vm.isCreditInvoice = !this.vm.isCreditInvoice;
    if (this.vm.isCreditInvoice) {
      this.vm.invoiceTypeName = $localize`:Credit Memo @@creditMemo:Credit Memo`;
      this.vm.title = $localize`:Credit Memo @@creditMemo:Credit Memo`;
    } else {
      this.vm.invoiceTypeName = $localize`:Sales Order @@salesOrder:Sales Order`;
      this.vm.title = $localize`:Sales Order @@salesOrder:Sales Order`;
    }
  }

  clearEntity(): void {
    this.vm.item.header.entity_id = '';
    this.vm.item.header.entityInput = '';
    this.vm.billToAddress = [];
    this.vm.shipToAddress = [];
    this.vm.contact = [];
  }

  selectEntityModal(entityName: any): void {
    this.vm.item.header.entity_id = '';
    const selectedEntity = (this.vm.entity || []).find((x: any) => x.name === entityName);
    if (!selectedEntity) {
      return;
    }
    const selectedEntityId = selectedEntity.id;
    this.vm.item.header.entity_id = selectedEntityId;
    this.vm.item.header.entityInput = entityName;
    this.setHeaderDateToTodayIfEmpty();
    this.processEntityData(selectedEntityId);
    this.termChange();
    this.validateDocumentData();
  }

  saveNewEntity(): void {
    let errorDescription = '';
    this.vm.entityButtonsDisabled = true;
    if (!this.vm.newEntity?.name) {
      errorDescription += $localize`:Name is required. @@nameisrequired:Name is required.` + '<br/>';
      this.vm.newEntityNameError = true;
    }
    if (!this.vm.newEntity?.type_id) {
      errorDescription += $localize`:Type is required. @@typeisrequired:Type is required.` + '<br/>';
      this.vm.newEntityTypeError = true;
    }
    if (!this.vm.newEntity?.receivable && !this.vm.newEntity?.payable) {
      this.vm.newEntityRPError = true;
      errorDescription += $localize`:At least one of receivable or payable is required. @@atleastoneofreceivableorpayableisrequired:At least one of receivable or payable is required.` + '<br/>';
    }
    if (errorDescription === '' && (this.permissionsService.Get('Admin') || this.permissionsService.Get('EditReceivableEntities'))) {
      this.tableDataService.post('entity', this.vm.newEntity).pipe(take(1)).subscribe({
        next: () => {
          this.vm.entityButtonsDisabled = false;
          this.vm.newEntity = {};
          this.tableDataService.getData('entity', '').pipe(take(1)).subscribe((r: any) => {
            this.vm.entity = r?.Data ?? r ?? [];
            this.spliceReceivableEntities();
          });
        },
        error: (err: any) => {
          this.vm.entityButtonsDisabled = false;
          console.error('[SO] saveNewEntity failed:', err);
        },
      });
    } else {
      this.vm.entityButtonsDisabled = false;
    }
  }

  saveNewAddress(): void {
    let errorDescription = '';
    this.vm.addressButtonsDisabled = true;
    this.vm.newAddress.entity_id = this.vm.item.header.entity_id;
    // Convert billing_address string value from the select dropdown to the numeric value the API expects
    if (this.vm.newAddress.billing_address === 'true' || this.vm.newAddress.billing_address === '1') {
      this.vm.newAddress.billing_address = 1;
    } else if (this.vm.newAddress.billing_address === 'false' || this.vm.newAddress.billing_address === '0') {
      this.vm.newAddress.billing_address = 0;
    }
    if (!this.vm.newAddress?.name) {
      errorDescription += $localize`:Name is required. @@nameisrequired:Name is required.` + '<br/>';
      this.vm.newAddressNameError = true;
    }
    if (errorDescription === '' && (this.permissionsService.Get('Admin') || this.permissionsService.Get('EditReceivableEntities'))) {
      this.tableDataService.post('entity_address', this.vm.newAddress).pipe(take(1)).subscribe({
        next: () => {
          this.vm.addressButtonsDisabled = false;
          this.vm.newAddress = {billing_address: 'true'};
          const entityId = this.vm.item?.header?.entity_id;
          if (entityId) {
            this.loadAndProcessEntityAddresses(entityId);
          }
        },
        error: (err: any) => {
          this.vm.addressButtonsDisabled = false;
          console.error('[SO] saveNewAddress failed:', err);
        },
      });
    } else {
      this.vm.addressButtonsDisabled = false;
    }
  }

  saveNewContact(): void {
    let errorDescription = '';
    this.vm.contactButtonsDisabled = true;
    this.vm.newContact.entity_id = this.vm.item.header.entity_id;
    if (!this.vm.newContact?.name) {
      errorDescription += $localize`:Name is required. @@nameisrequired:Name is required.` + '<br/>';
      this.vm.newContactNameError = true;
    }
    if (errorDescription === '' && (this.permissionsService.Get('Admin') || this.permissionsService.Get('EditReceivableEntities'))) {
      this.tableDataService.post('entity_contact', this.vm.newContact).pipe(take(1)).subscribe({
        next: () => {
          this.vm.contactButtonsDisabled = false;
          this.vm.newContact = {};
          const entityId = this.vm.item?.header?.entity_id;
          if (entityId) {
            this.tableDataService.getData('entity_contact_list', entityId + '?pageNumber=0&pageSize=999999').pipe(take(1)).subscribe((r: any) => {
              this.vm.contact = r?.Data ?? r ?? [];
            });
          }
        },
        error: (err: any) => {
          this.vm.contactButtonsDisabled = false;
          console.error('[SO] saveNewContact failed:', err);
        },
      });
    } else {
      this.vm.contactButtonsDisabled = false;
    }
  }

  updateInventoryItem(): void {
    this.tableDataService.getData('item', '?pageNumber=0&pageSize=999999').pipe(take(1)).subscribe((r: any) => {
      this.vm.inventoryItem = r?.Data ?? r ?? [];
      this.buildInventoryItemName();
      this.vm.item?.detail?.forEach((d: any) => {
        if (!d.control_line && d.item_id && !d.itemInput) {
          const match = (this.vm.inventoryItem ?? []).find((i: any) => i.id == d.item_id);
          if (match) {
            d.itemInput = match.code;
          }
        }
      });
    });
  }

  selectItemModal(itemId: any): void {
    const id = this.vm.tempItemDetailId;
    const detail = this.vm.item.detail ?? [];
    for (const d of detail) {
      if (d.id == id) {
        const item = (this.vm.inventoryItem ?? []).find((i: any) => i.id == itemId);
        if (!item) {
          break;
        }
        d.header_id = this.vm.item.header.id;
        d.description = item.name;
        d.itemInput = item.code;
        d.item_id = item.id;
        d.uom_id = item.uom_id;
        if (!d.quantity || d.quantity === '') {
          d.quantity = 1;
        }
        d.amount = this.changeToDecimal(Number(item.price));
        // Apply taxes: entity taxes take priority; fall back to item taxes if entity has none
        const applyTaxSlot = (slot: 1 | 2, entityTaxId: any, itemTaxId: any) => {
          const idField = slot === 1 ? 'tax_id_1' : 'tax_id_2';
          const sourceId = entityTaxId ?? itemTaxId;
          if (!sourceId) {
            d[idField] = null;
            return;
          }
          const tax = this.taxService.getTaxById(sourceId, this.vm.tax ?? []);
          if (this.taxService.isTaxUsable(sourceId, this.vm.tax ?? [], this.vm.taxRate ?? [])) {
            d[idField] = sourceId;
          } else {
            if (tax && this.taxService.isTaxExpired(tax)) {
              this.notification.showWarningToast(`Tax "${tax.name ?? ''}" is expired and was not applied.`);
            }
            d[idField] = null;
          }
        };
        applyTaxSlot(1, this.vm.entityTax1Id, item.receivable_tax_1_id);
        applyTaxSlot(2, this.vm.entityTax2Id, item.receivable_tax_2_id);
        if (item.revenue_account_id) {
          d.account_id = item.revenue_account_id;
          const acct = this.getAccountById(item.revenue_account_id);
          if (acct) {
            d.accountInput = `${acct.number} ${acct.name}`;
          }
        } else if (this.vm.entityAccountId) {
          d.account_id = this.vm.entityAccountId;
          const acct = this.getAccountById(this.vm.entityAccountId);
          d.accountInput = acct ? `${acct.number} ${acct.name}` : (this.vm.entityAccountName ?? '');
        }
        this.calculateTaxValues(d.id).then();
        this.calcTotal();
        break;
      }
    }
  }

  selectItemDropdown(id: any): void {
    const detail = this.vm.item.detail ?? [];
    for (const d of detail) {
      if (d.id == id && d.itemInput) {
        const item = (this.vm.inventoryItem ?? []).find((i: any) => i.code === d.itemInput);
        if (item) {
          d.item_id = item.id;
          d.description = item.name;
          d.uom_id = item.uom_id;
          if (!d.quantity || d.quantity === '') {
            d.quantity = 1;
          }
          d.amount = this.changeToDecimal(Number(item.price));
          // Apply taxes: entity taxes take priority; fall back to item taxes if entity has none
          const applyTaxSlotDD = (slot: 1 | 2, entityTaxId: any, itemTaxId: any) => {
            const idField = slot === 1 ? 'tax_id_1' : 'tax_id_2';
            const sourceId = entityTaxId ?? itemTaxId;
            if (!sourceId) {
              d[idField] = null;
              return;
            }
            const tax = this.taxService.getTaxById(sourceId, this.vm.tax ?? []);
            if (this.taxService.isTaxUsable(sourceId, this.vm.tax ?? [], this.vm.taxRate ?? [])) {
              d[idField] = sourceId;
            } else {
              if (tax && this.taxService.isTaxExpired(tax)) {
                this.notification.showWarningToast(`Tax "${tax.name ?? ''}" is expired and was not applied.`);
              }
              d[idField] = null;
            }
          };
          applyTaxSlotDD(1, this.vm.entityTax1Id, item.receivable_tax_1_id);
          applyTaxSlotDD(2, this.vm.entityTax2Id, item.receivable_tax_2_id);
          if (item.revenue_account_id) {
            d.account_id = item.revenue_account_id;
            const acct = this.getAccountById(item.revenue_account_id);
            if (acct) {
              d.accountInput = `${acct.number} ${acct.name}`;
            }
          } else if (this.vm.entityAccountId) {
            d.account_id = this.vm.entityAccountId;
            const acct = this.getAccountById(this.vm.entityAccountId);
            d.accountInput = acct ? `${acct.number} ${acct.name}` : (this.vm.entityAccountName ?? '');
          }
          this.calculateTaxValues(d.id).then();
          this.calcTotal();
        } else {
          d.itemInput = '';
        }
        break;
      }
    }
  }

  saveNewItem(): void {
    let errorDescription = '';
    this.vm.itemButtonsDisabled = true;
    if (!this.vm.newItem?.name) {
      errorDescription += $localize`:Name is required. @@nameisrequired:Name is required.` + '<br/>';
      this.vm.newItemNameError = true;
    }
    if (!this.vm.newItem?.code) {
      errorDescription += $localize`:Code is required. @@codeisrequired:Code is required.` + '<br/>';
      this.vm.newItemCodeError = true;
    }
    if (errorDescription === '' && (this.permissionsService.Get('Admin') || this.permissionsService.Get('EditItems'))) {
      this.tableDataService.post('item', this.vm.newItem).pipe(take(1)).subscribe({
        next: () => {
          this.vm.itemButtonsDisabled = false;
          this.vm.newItem = {};
          this.updateInventoryItem();
        },
        error: (err: any) => {
          this.vm.itemButtonsDisabled = false;
          console.error('[SO] saveNewItem failed:', err);
        },
      });
    } else {
      this.vm.itemButtonsDisabled = false;
    }
  }

  updateAccount(): void {
    this.tableDataService.getData('account', '?pageNumber=0&pageSize=999999').pipe(take(1)).subscribe((r: any) => {
      this.vm.account = r?.Data ?? r ?? [];
      this.buildAccountName();
    });
  }

  selectAccountModal(accountId: any): void {
    const id = this.vm.tempAccountDetailId;
    const detail = this.vm.item.detail ?? [];
    for (const d of detail) {
      if (d.id == id) {
        const acct = (this.vm.account ?? []).find((a: any) => a.id == accountId);
        if (acct) {
          d.account_id = acct.id;
          d.accountInput = `${acct.number} ${acct.name}`;
        }
        break;
      }
    }
  }

  accountSelectedFromEvent(detail: any, event: any): void {
    const item = event?.item;
    if (!item) {
      return;
    }
    if (item.id) {
      detail.account_id = item.id;
      detail.accountInput = `${item.number ?? ''} ${item.name ?? ''}`.trim();
    } else {
      const acct = (this.vm.account ?? []).find((a: any) => `${a.number} ${a.name}` === item.name);
      if (acct) {
        detail.account_id = acct.id;
        detail.accountInput = `${acct.number} ${acct.name}`;
      }
    }
  }

  saveNewAccount(): void {
    let errorDescription = '';
    this.vm.accountButtonsDisabled = true;
    if (!this.vm.newAccount?.name) {
      errorDescription += $localize`:Name is required. @@nameisrequired:Name is required.` + '<br/>';
      this.vm.newAccountNameError = true;
    }
    if (!this.vm.newAccount?.number) {
      errorDescription += $localize`:Number is required. @@numberisrequired:Number is required.` + '<br/>';
      this.vm.newAccountNumberError = true;
    }
    if (!this.vm.newAccount?.subtotal_id) {
      errorDescription += $localize`:Subtotal is required. @@subtotalisrequired:Subtotal is required.` + '<br/>';
      this.vm.newAccountSubtotalError = true;
    }
    if (errorDescription === '' && this.permissionsService.Get('Admin')) {
      this.tableDataService.post('account', this.vm.newAccount).pipe(take(1)).subscribe({
        next: () => {
          this.vm.accountButtonsDisabled = false;
          this.vm.newAccount = {};
          this.updateAccount();
        },
        error: (err: any) => {
          this.vm.accountButtonsDisabled = false;
          console.error('[SO] saveNewAccount failed:', err);
        },
      });
    } else {
      this.vm.accountButtonsDisabled = false;
    }
  }

  addRows(qty: any): void {
    if (qty === '') {
      qty = 0;
    }
    let maxSortOrder = 0;
    for (const d of (this.vm.item.detail ?? [])) {
      if (d.sort_order > maxSortOrder) {
        maxSortOrder = d.sort_order;
      }
    }
    for (let x = 0; x < qty; x++) {
      maxSortOrder++;
      this.vm.item.detail.push({id: maxSortOrder, sort_order: maxSortOrder, quantity: null, debit_credit: NaN, showRF: false});
    }
  }

  deleteLine(id: any): void {
    const detail = this.vm.item.detail ?? [];
    for (let x = 0; x < detail.length; x++) {
      if (detail[x].id == id) {
        detail.splice(x, 1);
        this.calcTotal();
        break;
      }
    }
  }

  changeToDecimal(value: any): any {
    return this.calculationService.changeToDecimal(value);
  }

  paymentAmountCalc(): void {
    try {
      const applied: any[] = Array.isArray(this.vm?.item?.applied) ? this.vm.item.applied : [];
      const sum = applied.reduce((acc: number, r: any) => acc + Number(r?.applied_amount || 0), 0);
      this.vm.item.header.payment_applied_total = sum;
      const invoiceTotal = Math.abs(Number(this.vm.item.header.detail_total || 0));
      this.vm.paid = invoiceTotal > 0 && sum >= invoiceTotal;
    } catch { /* resilient */
    }
  }

  changeLocation(event: any): void {
    this.vm.item.header.location_id = event;
    this.vm.request = this.vm.request || {};
    this.vm.request.location_id = event;
    if (this.vm.item.header.company_id == undefined && this.vm.Preferences?.default_company_id) {
      this.vm.item.header.company_id = this.vm.Preferences.default_company_id;
    }
  }

  currencyChange(): void {
    if (this.vm.item.header.currency_id) {
      const match = (this.vm.currency ?? []).find((c: any) => c.id === this.vm.item.header.currency_id);
      if (match) {
        this.vm.item.header.currency_rate = match.rate;
      }
    }
  }

  checkIfDateAfterCutOffDate(dateToCheck: Date): boolean {
    if (!dateToCheck) {
      return false;
    }
    if (!this.vm.item.header.company_id) {
      this.notification.showWarningToast('No company selected');
      this.vm.item.header.header_date = '';
      return false;
    }
    const d = new Date(dateToCheck);
    const comp = (this.vm.company ?? []).find((c: any) => c.id === this.vm.item.header.company_id);
    if (!comp) {
      return true;
    }
    const cutOff = new Date(comp.cut_off_date);
    if (d.getTime() > cutOff.getTime()) {
      return true;
    }
    this.notification.showWarningToast(
      $localize`:Date must be after the Cut Off date @@datemustbeafterthecutoffdate:Date must be after the Cut Off date` + ' ' + cutOff.toLocaleDateString()
    );
    return false;
  }

  async saveNew(): Promise<void> {
    this.vm.newInvoice = true;
    await this.save();
    this.resetSnapshot();
    try {
      await this.router.navigate(['/transactions/invoice/quote/form'], {queryParams: {invoiceId: NEW_DOC_QUOTE}, replaceUrl: true});
    } catch { /* ignore */
    }
  }

  printDocument(): void {
    try {
      const docId = this.vm?.item?.header?.id;
      const companyId = this.vm?.item?.header?.company_id;
      if (!docId || !companyId) {
        return;
      }
      const totalVal = Number(this.vm?.item?.header?.total ?? this.vm?.item?.header?.detail_total ?? 0);
      const docType = 'quotes';
      const html = this.printBuilder.buildPrintHtml(this.vm, docType);
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
      const cleanup = () => setTimeout(() => {
        try {
          iframe.remove();
        } catch { /* ignore */
        }
      }, 400);
      const doPrint = () => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch { /* ignore */
        } finally {
          cleanup();
        }
      };
      iframe.onload = () => {
        try {
          const imgs = Array.from(iframe.contentWindow?.document?.images || []);
          if (!imgs.length) {
            doPrint();
            return;
          }
          let loaded = 0;
          imgs.forEach((img: any) => {
            if (img.complete) {
              if (++loaded >= imgs.length) {
                doPrint();
              }
            } else {
              img.addEventListener('load', () => {
                if (++loaded >= imgs.length) {
                  doPrint();
                }
              }, {once: true});
            }
          });
          setTimeout(doPrint, 3000);
        } catch {
          doPrint();
        }
      };
      (iframe as any).srcdoc = html;
      document.body.appendChild(iframe);
    } catch (e) {
      console.error('[SO] printDocument failed:', e);
    }
  }

  signature(sig: any): void {
    this.vm.item.header.signature = sig;
    this.vm.getSignature = true;
    try {
      document.getElementById('closeSignatureModel')?.click();
    } catch { /* ignore */
    }
    this.save().then();
  }

  deleteSignature(): void {
    this.vm.item.header.signature = '';
    this.vm.getSignature = true;
    try {
      document.getElementById('closeSignatureModel')?.click();
    } catch { /* ignore */
    }
    this.save().then();
  }

  gotAnUploadFile(_file: any): void { /* handled by file upload component */
  }

  syncItemAttachments(attachmentsArray: any): void {
    this.vm.item.attachments = [];
    (attachmentsArray ?? []).forEach((x: any) => {
      this.vm.item.attachments.push({
        id: x.id,
        header_id: x.header_id,
        file_name: x.fileName,
        type: x.fileType,
        attachment: x.fileData,
        audit_sequence: 0,
      });
    });
  }
}
